/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type Blueprint } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { AppRootContextProvider } from '@r-app/app-root-context-provider';
import { BlueprintSvgContextMenuWrapper } from './blueprint-svg-context-menu-wrapper';
import { ComponentController } from '@r-core/controller';
import { computeFittedViewBox } from './compute-fitted-viewbox';
import { CreateBlueprintSymbols } from '../../symbols';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { REFERENCE_WIDTH } from './viewport-constants';
import { RenderPanel } from '../../render/render-panel';
import { usePageFontFactorOverride } from './page-font-factor-context';
import { useSheetDimensionVisibility } from './use-sheet-dimension-visibility.hook';
import { useStabilizedInkExtent } from './use-stabilized-ink-extent.hook';

/**
 * Single-SVG renderer for the combined front+back page (Asado / FirePit).
 * Both panels share the same coordinate system, so the gap between them is
 * specified in model-inches — exactly matching the side-panels page convention
 * (`PANEL_GROUP_SPACING = 18` in `RenderPanels`).
 */

const PANEL_GROUP_SPACING = 18; // matches RenderPanels for side/back panel groupings
const TOP_LABEL_OFFSET = 8;
const SIDEWAYS_LABEL_OFFSET = 0.75;

export type CombinedPanelEntry = {
	panelId: string;
	topLabel?: string;
	/**
	 * When true, renders a rotated "Front" label on the inside edge of the
	 * panel (the edge facing the gap between panels) — exactly matching the
	 * side-panels page convention. The side and rotation are derived from
	 * the panel's position in the group (left vs right of center).
	 */
	showSidewaysFrontLabel?: boolean;
};

export type BlueprintCombinedPanelCardProps = {
	blueprint: Blueprint;
	islandId: string;
	pageIndex: number;
	panels: Array<CombinedPanelEntry>;
};

const BlueprintCombinedPanelCardInner = (props: BlueprintCombinedPanelCardProps): React.ReactElement => {
	const { blueprint, islandId, pageIndex, panels } = props;

	const mainContext = useContext(AppContext);
	const baseFactor = mainContext?.controller?.options?.dimensionFontSizeFactor ?? 1.2;

	const svgRef = useRef<SVGSVGElement>(null);
	const contentGroupRef = useRef<SVGGElement>(null);
	const [containerPx, setContainerPx] = useState<{ w: number; h: number }>({ w: 800, h: 400 });
	const [contentWidth, setContentWidth] = useState<number | null>(null);

	const pageFontFactorOverride = usePageFontFactorOverride();

	const zoomCompensation = contentWidth !== null
		? Math.max(1, contentWidth / REFERENCE_WIDTH)
		: 1;
	const computedFontFactor = baseFactor * zoomCompensation;
	const fontFactor = pageFontFactorOverride ?? computedFontFactor;

	// Mirror the main controller's display options so this isolated viewport
	// reproduces the regular MFG view exactly (hidden dimensions, dimension
	// mode, etc.). Same option list as BlueprintPanelCard.
	const mainOptions = mainContext?.controller?.options;
	const controller = useMemo((): ComponentController => {
		const ctrl = new ComponentController(blueprint, {
			eventGroup: `bp-combined-${pageIndex}-${blueprint.id}`
		});

		ctrl.navigation.displayMode = DisplayMode.Manufacturing;
		ctrl.navigation.islandId = islandId;
		// Navigation panelId points at the first panel; the SVG renders both
		// directly via <RenderPanel> with offsets (bypassing DisplayPanelMode).
		ctrl.navigation.panelId = panels[0]?.panelId ?? null;

		if (mainOptions) {
			const mirrored: Array<keyof typeof mainOptions> = [
				'showDimensions', 'showApplianceDimensions', 'showAllDimensions',
				'showDimensionsMode', 'showCutouts', 'showAppliances',
				'showApplianceLabels', 'showBreakpoints', 'showCorners',
				'showCountertops', 'showHints', 'showIslandBaseDimensions',
				'showOverheadDimensions', 'showOverlap', 'showObstructions',
				'showBacksplashes', 'showPanelInsideDimensions',
				'showPanelOutsideDimensions', 'showApplianceGroups',
				'highlightFrontPanel', 'dimensionOrientationMode',
				'applianceOpacity', 'cutoutOpacity', 'borderColor',
				'cutoutColor', 'backgroundColor', 'useFractions',
				'assetsBaseUrl', 'assetsSasToken'
			] as const;
			mirrored.forEach((key) => {
				const value = (mainOptions as unknown as Record<string, unknown>)[key];
				if (value !== undefined) {
					(ctrl.options as unknown as Record<string, unknown>)[key] = value;
				}
			});
		}

		ctrl.options.showDimensions = true;
		ctrl.options.showAppliances = true;
		ctrl.options.showBreakpoints = true;
		ctrl.options.isBlueprintView = true;
		ctrl.options.readonly = false;
		ctrl.options.contextMenu = true;
		ctrl.options.backgroundColor = '#FFFFFF';

		ctrl.options.dimensionFontSizeFactor = fontFactor;
		ctrl.options.dimensions = { fontSizeFactor: fontFactor };

		return ctrl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blueprint, islandId, pageIndex, panels]);

	useSheetDimensionVisibility(controller);

	if (controller.options.dimensionFontSizeFactor !== fontFactor) {
		controller.options.dimensionFontSizeFactor = fontFactor;
		controller.options.dimensions = { fontSizeFactor: fontFactor };
	}

	const { kitchen } = controller;
	const island = kitchen?.design.islands.findById(islandId);

	// Resolve panel objects in render order; bail out gracefully if missing.
	const panelObjects = panels
		.map((entry) => ({ entry, panel: island?.panels.findById(entry.panelId) ?? null }))
		.filter((p): p is { entry: CombinedPanelEntry; panel: NonNullable<typeof p.panel> } => p.panel !== null);

	// Compute group geometry: total width spans both panels + spacing in between.
	// `cursor` walks left → right placing each panel's center at the correct offset.
	const totalGroupWidth = panelObjects.reduce((sum, { panel }) => sum + panel.size.width, 0)
		+ (PANEL_GROUP_SPACING * Math.max(0, panelObjects.length - 1));
	const panelTrueHeight = island?.islandSize.height ?? 34.5;

	let cursor = -totalGroupWidth / 2;
	const placements = panelObjects.map(({ entry, panel }) => {
		const offsetX = cursor + (panel.size.width / 2);
		cursor += panel.size.width + PANEL_GROUP_SPACING;
		return { entry, panel, offsetX };
	});

	useEffect(() => {
		setContentWidth((prev) => prev ?? totalGroupWidth);
	}, [totalGroupWidth]);

	useEffect(() => {
		const el = svgRef.current;
		if (!el) return;

		const update = (): void => {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				setContainerPx({ w: rect.width, h: rect.height });
			}
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const inkExtent = useStabilizedInkExtent({
		groupRef: contentGroupRef,
		resetKey: `${islandId}|${pageIndex}|${fontFactor}`
	});

	// Ruler scale against exact group geometry first; the measured annotation ink
	// only ever shrinks it, and only when it would otherwise cross the page edge.
	// Ink is symmetrized vertically around the panels' own center before it drives
	// the fit so a TOP_ONLY appliance label above the front panel (Asado Stand's
	// C1CHCS -- #422 / #535) can't slide both panels down together on the sheet.
	// Whichever side of the panels has more ink dictates a matching reserved band
	// on the other side, so the ink center lands on the panel center and the label
	// just paints in the (previously blank) upper band. Horizontal ink is left
	// alone -- the two-panel row is already symmetric on that axis.
	const viewBox = useMemo((): string => {
		const panelCenterY = -panelTrueHeight / 2;
		const symmetricInk = inkExtent === null
			? null
			: {
				left: inkExtent.left,
				right: inkExtent.right,
				top: panelCenterY - Math.max(panelCenterY - inkExtent.top, inkExtent.bottom - panelCenterY),
				bottom: panelCenterY + Math.max(panelCenterY - inkExtent.top, inkExtent.bottom - panelCenterY)
			};
		return computeFittedViewBox({
			geometry: {
				left: -totalGroupWidth / 2,
				right: totalGroupWidth / 2,
				top: -panelTrueHeight,
				bottom: 0
			},
			ink: symmetricInk,
			containerPx
		});
	}, [containerPx, totalGroupWidth, panelTrueHeight, inkExtent]);

	useEffect(() => {
		return () => { controller?.destroy(); };
	}, [controller]);

	const labelFontSize = fontFactor * 2;

	return (
		<AppRootContextProvider controller={controller}>
			<BlueprintSvgContextMenuWrapper svgRef={svgRef}>
				<svg
					ref={svgRef}
					xmlns="http://www.w3.org/2000/svg"
					style={{ width: '100%', height: '100%', display: 'block', background: 'white' }}
					viewBox={viewBox}
					preserveAspectRatio="xMidYMid meet"
				>
					<CreateBlueprintSymbols />
					<g ref={contentGroupRef}>
						{placements.map(({ entry, panel, offsetX }) => {
							// Match RenderPanels exactly: sign comes from the panel's center x.
							// Label sits on the INSIDE edge (opposite the sign), rotated so it
							// reads outward (sign × -90°) — same visual as the side-panels page.
							const sign = offsetX < 0 ? -1 : 1;
							const sidewaysX = offsetX - (sign * ((panel.size.width / 2) + SIDEWAYS_LABEL_OFFSET));
							const sidewaysY = -(panel.size.height / 2);
							return (
								<g key={panel.id}>
									<RenderPanel panelId={panel.id} islandId={islandId} offsetX={offsetX} />
									{entry.topLabel && (
										<text
											x={offsetX}
											y={-(panel.size.height) - TOP_LABEL_OFFSET}
											textAnchor="middle"
											fontSize={labelFontSize}
											fontWeight="bold"
											className="rta-designer-component-measure rta-noselect"
											pointerEvents="none"
										>
											{entry.topLabel}
										</text>
									)}
									{entry.showSidewaysFrontLabel && (
										<text
											x={sidewaysX}
											y={sidewaysY}
											transform={`rotate(${sign * -90}, ${sidewaysX}, ${sidewaysY})`}
											textAnchor="middle"
											fontSize={labelFontSize}
											fontWeight="bold"
											className="rta-designer-component-measure rta-noselect"
											pointerEvents="none"
										>
											Front
										</text>
									)}
								</g>
							);
						})}
					</g>
				</svg>
			</BlueprintSvgContextMenuWrapper>
		</AppRootContextProvider>
	);
};

export const BlueprintCombinedPanelCard = React.memo(
	BlueprintCombinedPanelCardInner,
	(prev, next) =>
		prev.islandId === next.islandId
		&& prev.pageIndex === next.pageIndex
		&& prev.panels.length === next.panels.length
		&& prev.panels.every((p, i) =>
			p.panelId === next.panels[i].panelId
			&& p.topLabel === next.panels[i].topLabel
			&& p.showSidewaysFrontLabel === next.panels[i].showSidewaysFrontLabel
		)
);
